import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Modal form that posts to POST /api/enquiries/fleet.
 * Captures the essentials the sales team needs to qualify a fleet lead.
 */
export const FleetEnquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    mobile: "",
    city: "",
    fleet_size: "",
    use_case: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries/fleet", form);
      setDone(true);
      toast.success("Enquiry sent! Our fleet team will reach out within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit the enquiry. Please try again.");
    }
    setLoading(false);
  };

  const close = () => {
    setDone(false);
    setForm({
      name: "",
      company: "",
      email: "",
      mobile: "",
      city: "",
      fleet_size: "",
      use_case: "",
      message: "",
    });
    onClose();
  };

  return (
    <div
      data-testid="fleet-enquiry-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-8 overflow-y-auto"
      onClick={close}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-8 relative my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="fleet-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-[#3DDC97]/15 flex items-center justify-center text-[#0d8f5b] mx-auto mb-5">
              <Check size={26} />
            </div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight">Enquiry received</h2>
            <p className="text-gray-600 text-sm mt-2 max-w-sm mx-auto">
              Thanks {form.name || "there"} — our fleet team will reach out at{" "}
              <span className="font-semibold text-ink">{form.email}</span> within 24 business hours.
            </p>
            <button
              onClick={close}
              data-testid="fleet-modal-done"
              className="btn-primary justify-center !py-3 !px-6 !text-sm mt-6 inline-flex"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl bg-brand-gradient flex items-center justify-center text-white mb-4">
              <Send size={20} />
            </div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight">Enquire Fleet Solutions</h2>
            <p className="text-gray-600 text-sm mt-2">
              Tell us about your fleet needs. Our team will craft a custom plan.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Name*</Label>
                  <Input required data-testid="fleet-name-input" value={form.name} onChange={set("name")} placeholder="Aarav Mehta" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Company</Label>
                  <Input data-testid="fleet-company-input" value={form.company} onChange={set("company")} placeholder="Acme Delivery" className="h-11 rounded-xl" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Email*</Label>
                  <Input required type="email" data-testid="fleet-email-input" value={form.email} onChange={set("email")} placeholder="you@company.com" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Mobile*</Label>
                  <Input required data-testid="fleet-mobile-input" value={form.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" className="h-11 rounded-xl" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">City</Label>
                  <Input data-testid="fleet-city-input" value={form.city} onChange={set("city")} placeholder="Bangalore" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Fleet size</Label>
                  <Input data-testid="fleet-size-input" value={form.fleet_size} onChange={set("fleet_size")} placeholder="e.g. 20-50 vehicles" className="h-11 rounded-xl" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Use case</Label>
                <Input data-testid="fleet-usecase-input" value={form.use_case} onChange={set("use_case")} placeholder="Food delivery, corporate commute, hotel rentals…" className="h-11 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Message</Label>
                <Textarea
                  data-testid="fleet-message-input"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Anything else we should know?"
                  className="min-h-[80px] rounded-xl"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="fleet-submit-btn"
                className="btn-primary w-full justify-center !py-3.5 !text-base"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Submit enquiry"}
              </button>
              <p className="text-[11px] text-gray-500 text-center leading-relaxed">
                By submitting, you agree to be contacted by our fleet team via email, phone or WhatsApp.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
